/**
 * Geo service — calls /api/v1/geo/... (no auth required).
 * Same endpoints Android/iOS use.
 */

import axios from "axios";
import { env } from "../../config/env";

// ── Types ──────────────────────────────────────────────────────────────────

export interface Country {
  id: number;
  name: string;
  iso2: string;
  iso3: string;
  phonecode: string;    // e.g. "91"  (no plus)
  phone_regex?: string; // Optional regex for phone validation
  flag: string;         // emoji flag
  capital: string;
  currency: string;
  currency_name: string;
  currency_symbol: string;
  region: string;
  subregion: string;
  timezones: { zoneName: string; gmtOffset: number; gmtOffsetName: string; abbreviation: string; tzName: string }[];
  latitude: string | null;
  longitude: string | null;
}

export interface State {
  name: string;
  isoCode: string;
  countryCode: string;
}

export interface City {
  name: string;
}

/** A continent/region or subregion shortcut (e.g. "Europe", "Caribbean"),
 * mirrors GET /api/v1/geo/regions — the same source of truth the backend
 * uses to resolve a seller's declared market or a deal's destination when
 * it's written as a region name rather than individual countries. */
export interface GeoRegion {
  key: string;
  label: string;
  aliases: string[];
  country_iso2: string[];
  countries: string[];
}

// ── HTTP client (unauthenticated, no token injection) ──────────────────────

const http = axios.create({ baseURL: env.apiBaseUrl });

// ── In-memory cache to avoid hammering the API on re-renders ──────────────

const _cache = new Map<string, unknown>();

async function _get<T>(url: string): Promise<T> {
  if (_cache.has(url)) return _cache.get(url) as T;
  const { data } = await http.get<T>(url);
  _cache.set(url, data);
  return data;
}

// ── Service ────────────────────────────────────────────────────────────────

export const geoService = {
  /** All 250 countries with phonecode, flag, currency, timezones. */
  getCountries(search?: string): Promise<Country[]> {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    return _get<Country[]>(`/api/v1/geo/countries${qs}`);
  },

  /** States / provinces for a country ISO-2 code. Empty array if none. */
  getStates(countryIso2: string): Promise<State[]> {
    return _get<State[]>(`/api/v1/geo/states/${countryIso2.toUpperCase()}`);
  },

  /**
   * Cities for a state (when country has states),
   * or all cities for a country (when it has no states).
   */
  getCities(countryIso2: string, stateIso2?: string): Promise<City[]> {
    const path = stateIso2
      ? `/api/v1/geo/cities/${countryIso2.toUpperCase()}/${stateIso2.toUpperCase()}`
      : `/api/v1/geo/cities/${countryIso2.toUpperCase()}`;
    return _get<City[]>(path);
  },

  /** Region/subregion shortcuts (Europe, Caribbean, etc.) with their full country list. */
  getRegions(): Promise<GeoRegion[]> {
    return _get<GeoRegion[]>("/api/v1/geo/regions");
  },
};
